import { GroupInfo, GroupParticipant, GroupMatch } from '@/hooks/useUserGroupInfo';

interface Team {
  teamOrder: number;
  name?: string;
  player1?: string;
  player2?: string;
  registrationId?: string;
}

interface ScheduleMatch {
  matchNumber: number;
  team1: string;
  team2: string;
  time: string;
  day: string;
}

interface GroupCalendar {
  group_name: string;
  category: string;
  subcategory?: string;
  day?: string; // "Jueves 30 de octubre"
  start_time?: string; // "18:00"
  court_name?: string;
  teams: Team[];
  schedule_matrix: {
    matches: ScheduleMatch[];
    teams?: Team[];
  };
}

interface Registration {
  id: string;
  user_id: string;
  partner_user_id: string | null;
  player1_name?: string;
  player2_name?: string;
}

/**
 * Parses group_calendars data to extract group-specific information
 */
export function parseGroupScheduleData(
  groupCalendar: GroupCalendar,
  currentUserId: string,
  registrations: Registration[]
): GroupInfo {
  console.log('[GroupParser] Processing group calendar:', {
    groupName: groupCalendar.group_name,
    teamsCount: groupCalendar.teams?.length || 0,
    matchesCount: groupCalendar.schedule_matrix?.matches?.length || 0
  });

  console.log('[GroupParser] Raw schedule_matrix:', {
    matchesCount: groupCalendar.schedule_matrix?.matches?.length,
    firstMatch: groupCalendar.schedule_matrix?.matches?.[0],
    teamsInMatrix: groupCalendar.schedule_matrix?.teams?.length
  });

  try {
    // Helper to check if a name is a placeholder
    const isPlaceholder = (name: string | undefined): boolean => {
      if (!name) return true;
      const normalized = name.toLowerCase().trim();
      return normalized === 'por asignar' || 
             normalized.includes('placeholder') ||
             normalized === '';
    };

    // Helper to get real names - prioritize registrations data
    const getTeamNames = (team: Team): { player1: string; player2?: string } => {
      // First, try to find the registration for this team
      if (team.registrationId) {
        const reg = registrations.find(r => r.id === team.registrationId);
        if (reg?.player1_name && !isPlaceholder(reg.player1_name)) {
          console.log('[GroupParser] Using registration names for team:', {
            registrationId: team.registrationId,
            player1: reg.player1_name,
            player2: reg.player2_name
          });
          return {
            player1: reg.player1_name,
            player2: reg.player2_name || undefined
          };
        }
      }

      // Fallback: parse from team.name or team.player1
      const teamName = team.name || team.player1 || '';
      
      // If teamName is a placeholder, try to find by teamOrder
      if (isPlaceholder(teamName)) {
        // Try to match by order in registrations array
        const regByOrder = registrations[team.teamOrder - 1];
        if (regByOrder?.player1_name && !isPlaceholder(regByOrder.player1_name)) {
          console.log('[GroupParser] Using registration by order for team:', {
            teamOrder: team.teamOrder,
            player1: regByOrder.player1_name,
            player2: regByOrder.player2_name
          });
          return {
            player1: regByOrder.player1_name,
            player2: regByOrder.player2_name || undefined
          };
        }
        return { player1: 'Por asignar', player2: undefined };
      }

      // Parse "Player1 / Player2" format
      const names = teamName.split(' / ');
      return {
        player1: names[0]?.trim() || 'Por asignar',
        player2: names[1]?.trim()
      };
    };

    // Prioritize teams from the top-level (usually more up-to-date)
    const teamsSource = groupCalendar.teams?.length > 0 
      ? groupCalendar.teams 
      : groupCalendar.schedule_matrix?.teams || [];

    // Build participants list from teams
    const participants: GroupParticipant[] = teamsSource
      .sort((a, b) => a.teamOrder - b.teamOrder)
      .map((team) => {
        const { player1, player2 } = getTeamNames(team);
        
        // Check if this team includes the current user
        const isUser = registrations.some(reg => 
          reg.id === team.registrationId && 
          (reg.user_id === currentUserId || reg.partner_user_id === currentUserId)
        );

        return {
          id: team.registrationId || `team-${team.teamOrder}`,
          letter: String.fromCharCode(65 + team.teamOrder - 1), // teamOrder 1 = A
          player1,
          player2,
          isUser
        };
      });

    // Helper function to normalize team names for comparison
    const normalizeTeamName = (name: string): string => {
      return name
        .trim()                          // Remove leading/trailing spaces
        .toLowerCase()                   // Case-insensitive FIRST
        .replace(/\s*\/\s*$/g, '')      // Remove trailing " / " or "/" (GLOBAL)
        .replace(/\/\s*$/g, '')         // Remove trailing "/" without space (GLOBAL)
        .replace(/\s*\/\s*/g, ' / ')    // Normalize ALL " / " to consistent format (GLOBAL)
        .replace(/\s+/g, ' ')           // Normalize multiple spaces to single space
        .trim();                         // Final trim after all transformations
    };

    // Parse matches from schedule_matrix
    const matches: GroupMatch[] = (groupCalendar.schedule_matrix?.matches || []).map(match => {
      // For round-robin with 3 teams, the match pattern is always:
      // Match 1: Team 1 vs Team 2 (A vs B)
      // Match 2: Team 1 vs Team 3 (A vs C)
      // Match 3: Team 2 vs Team 3 (B vs C)
      
      let team1Participant: GroupParticipant | undefined;
      let team2Participant: GroupParticipant | undefined;
      
      const matchNum = match.matchNumber;
      
      if (participants.length === 3) {
        // Standard round-robin pattern - map by match number
        switch(matchNum) {
          case 1:
            team1Participant = participants[0]; // A
            team2Participant = participants[1]; // B
            break;
          case 2:
            team1Participant = participants[0]; // A
            team2Participant = participants[2]; // C
            break;
          case 3:
            team1Participant = participants[1]; // B
            team2Participant = participants[2]; // C
            break;
          default:
            console.warn('[GroupParser] Unexpected match number:', matchNum);
            team1Participant = participants[0];
            team2Participant = participants[1];
        }
        
        console.log('[GroupParser] Match mapped by number:', {
          matchNumber: matchNum,
          team1FromSupabase: match.team1,
          team2FromSupabase: match.team2,
          assignedTeam1Letter: team1Participant?.letter,
          assignedTeam1Name: `${team1Participant?.player1}${team1Participant?.player2 ? ' / ' + team1Participant.player2 : ''}`,
          assignedTeam2Letter: team2Participant?.letter,
          assignedTeam2Name: `${team2Participant?.player1}${team2Participant?.player2 ? ' / ' + team2Participant.player2 : ''}`,
          time: match.time
        });
      } else {
        // Fallback: try to match by name for non-standard group sizes
        const normalizeTeamName = (name: string): string => {
          return name
            .trim()
            .toLowerCase()
            .replace(/\s*\/\s*$/g, '')
            .replace(/\/\s*$/g, '')
            .replace(/\s*\/\s*/g, ' / ')
            .replace(/\s+/g, ' ')
            .trim();
        };
        
        team1Participant = participants.find(p => {
          const fullName = `${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`;
          return normalizeTeamName(fullName) === normalizeTeamName(match.team1);
        });
        
        team2Participant = participants.find(p => {
          if (p.id === team1Participant?.id) return false;
          const fullName = `${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`;
          return normalizeTeamName(fullName) === normalizeTeamName(match.team2);
        });
        
        console.log('[GroupParser] Match mapped by name (fallback):', {
          matchNumber: matchNum,
          team1Letter: team1Participant?.letter,
          team2Letter: team2Participant?.letter
        });
      }

      return {
        id: `${groupCalendar.group_name}-${match.matchNumber}`,
        matchId: `${groupCalendar.group_name}-${match.team1}-vs-${match.team2}`,
        participants: [
          team1Participant?.letter || 'A',
          team2Participant?.letter || 'B'
        ] as [string, string],
        time: match.time,
        date: match.day || groupCalendar.day || '',
        court: groupCalendar.court_name
      };
    });

    // Calculate end time (assuming 3 hours of play if not specified)
    let endTime = '';
    if (groupCalendar.start_time) {
      const [hours, minutes] = groupCalendar.start_time.split(':').map(Number);
      const endHours = hours + 3;
      endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    const groupInfo: GroupInfo = {
      groupName: groupCalendar.group_name,
      category: groupCalendar.category,
      subcategory: groupCalendar.subcategory,
      date: groupCalendar.day || '',
      startTime: groupCalendar.start_time || '',
      endTime: endTime,
      court: groupCalendar.court_name,
      participants,
      matches
    };

    console.log('[GroupParser] ✅ Processed group info:', {
      groupName: groupInfo.groupName,
      participantsCount: participants.length,
      matchesCount: matches.length
    });

    return groupInfo;
  } catch (error) {
    console.error('[GroupParser] ❌ Error processing group calendar:', error);
    
    // Return minimal valid group info
    return {
      groupName: groupCalendar.group_name,
      category: groupCalendar.category || '',
      subcategory: groupCalendar.subcategory,
      date: groupCalendar.day || '',
      startTime: groupCalendar.start_time || '',
      endTime: '',
      court: groupCalendar.court_name,
      participants: [],
      matches: []
    };
  }
}

/**
 * Formats a date string to a readable format
 * @param dateStr - Date string in format "2025-10-24"
 * @returns Formatted date like "Viernes - 24 de octubre"
 */
export function formatScheduleDate(dateStr: string): string {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    
    const dayName = dayNames[date.getDay()];
    const day = date.getDate();
    const monthName = monthNames[date.getMonth()];
    
    return `${dayName} - ${day} de ${monthName}`;
  } catch (error) {
    return dateStr;
  }
}
